// RITA-1525 (Teilpaket 2) — Bearbeitungsoperationen auf dem Vertragsdokument.
//
// Alle Operationen sind rein: Sie erhalten Dokument und Auswahl, liefern ein neues Dokument und die
// Auswahl danach; die Eingaben bleiben unverändert (Grundlage für Rückgängig/Wiederholen). Positionen
// werden vorab geklemmt, damit der Tastaturpfad nie eine Ausnahme wirft. Platzhalter und Kästchen
// sind eine Einheit und werden nur als Ganzes gelöscht oder verschoben. Nach jeder Operation wird
// normalisiert (Normalform): angrenzende Textläufe gleichen Formats verschmelzen, leere Textläufe
// entfallen, direkt aufeinanderfolgende Listen werden eine Liste, Seitenumbrüche am Textanfang oder
// doppelt entfallen (sie hätten in der Seitenaufteilung keine Wirkung), der Text hat immer einen
// Absatz oder eine Liste, ein leerer Kopf/Fuß ist []. Liefert eine Operation keine Änderung, gibt sie
// dasselbe Dokumentobjekt zurück.
import {
  absatz,
  KAESTCHEN_ANGEKREUZT,
  KAESTCHEN_LEER,
  liste as neueListe,
  platzhalter as platzhalterknoten,
  seitenumbruch as neuerSeitenumbruch,
  text as textlauf,
  zeilenumbruch,
} from './erzeugen';
import {
  anfangVon,
  anzahlEintraege,
  Auswahl,
  auswahlEntHaengen,
  auswahlKlemmen,
  auswahlSortiert,
  Bereich,
  bloeckeVon,
  blockLaenge,
  Formatmerkmale,
  formatAnStelle,
  formatVon,
  formateGleich,
  Formattraeger,
  inhaltLaenge,
  inhaltVon,
  istFormattraeger,
  istKollabiert,
  knotenLaenge,
  kollabiert,
  mitBloecken,
  mitGroesse,
  OHNE_FORMAT,
  Position,
  position,
  stelleAnOffset,
} from './positionen';
import { eigeneGroesse, grundgroesseVon } from './schriftgroessen';
import { Block, Inlineknoten, Liste, Vertragsdokument } from './types';

export interface Bearbeitungsergebnis {
  dokument: Vertragsdokument;
  auswahl: Auswahl;
}

/** Umschaltbare Merkmale (Fett/Kursiv/Unterstrichen); die Schriftgröße ist ein Wert und wird mit `groesseSetzen` gesetzt. */
export type Formatmerkmal = 'fett' | 'kursiv' | 'unterstrichen';

const PRAEFIX: Record<Bereich, string> = { text: 'b', kopf: 'k', fuss: 'f' };
const ALLE_BEREICHE: readonly Bereich[] = ['text', 'kopf', 'fuss'];

/** Liefert fortlaufend neue, im ganzen Dokument eindeutige Block-Kennungen (`b7`, `k2`, …). */
export function kennungsquelle(dokument: Vertragsdokument, bereich: Bereich = 'text'): () => string {
  const alle = new Set<string>();
  let zaehler = 0;
  ALLE_BEREICHE.forEach((name) => {
    bloeckeVon(dokument, name).forEach((block) => {
      alle.add(block.id);
      const treffer = /^[bkf](\d{1,15})$/.exec(block.id);
      if (treffer) zaehler = Math.max(zaehler, Number(treffer[1]));
    });
  });
  return () => {
    let kennung = '';
    do {
      zaehler += 1;
      kennung = `${PRAEFIX[bereich]}${zaehler}`;
    } while (alle.has(kennung));
    alle.add(kennung);
    return kennung;
  };
}

export function neueKennung(dokument: Vertragsdokument, bereich: Bereich = 'text'): string {
  return kennungsquelle(dokument, bereich)();
}

// ---------------------------------------------------------------------------------------------
// Graphemcluster (Emoji, Flaggen, zusammengesetzte Zeichen werden als Ganzes gelöscht)

interface Segment {
  index: number;
  segment: string;
}
interface Segmentierer {
  segment: (text: string) => Iterable<Segment>;
}
type IntlMitSegmenter = { Segmenter?: new (sprache: string, optionen: { granularity: string }) => Segmentierer };

const segmentierer: Segmentierer | null = (() => {
  const intl = Intl as unknown as IntlMitSegmenter;
  return intl.Segmenter ? new intl.Segmenter('de', { granularity: 'grapheme' }) : null;
})();

const istHochSurrogat = (zeichen: string): boolean => zeichen >= '\ud800' && zeichen <= '\udbff';
const istTiefSurrogat = (zeichen: string): boolean => zeichen >= '\udc00' && zeichen <= '\udfff';

/** Länge des Zeichens (Graphemcluster) vor der Stelle `innen` im Text. */
export function graphemVor(text: string, innen: number): number {
  if (innen <= 0) return 0;
  if (segmentierer) {
    const segmente = Array.from(segmentierer.segment(text.slice(0, innen)));
    const letztes = segmente[segmente.length - 1];
    return letztes ? innen - letztes.index : 1;
  }
  if (innen >= 2 && istTiefSurrogat(text[innen - 1]) && istHochSurrogat(text[innen - 2])) return 2;
  return 1;
}

/** Länge des Zeichens (Graphemcluster) nach der Stelle `innen` im Text. */
export function graphemNach(text: string, innen: number): number {
  if (innen >= text.length) return 0;
  if (segmentierer) {
    const erstes = Array.from(segmentierer.segment(text.slice(innen)))[0];
    return erstes ? erstes.segment.length : 1;
  }
  if (istHochSurrogat(text[innen]) && istTiefSurrogat(text[innen + 1] ?? '')) return 2;
  return 1;
}

// ---------------------------------------------------------------------------------------------
// Inline-Inhalt

function mitFormat(knoten: Formattraeger, format: Formatmerkmale): Formattraeger {
  return knoten.typ === 'text' ? textlauf(knoten.text, format) : platzhalterknoten(knoten.schluessel, format);
}

/** Verschmilzt angrenzende Textläufe gleichen Formats und entfernt leere Textläufe. */
export function normalisiereInhalt(inhalt: Inlineknoten[]): Inlineknoten[] {
  const ergebnis: Inlineknoten[] = [];
  inhalt.forEach((knoten) => {
    if (knoten.typ === 'text' && knoten.text === '') return;
    const letzter = ergebnis[ergebnis.length - 1];
    if (
      knoten.typ === 'text' &&
      letzter &&
      letzter.typ === 'text' &&
      formateGleich(formatVon(letzter), formatVon(knoten))
    ) {
      ergebnis[ergebnis.length - 1] = textlauf(letzter.text + knoten.text, formatVon(letzter));
      return;
    }
    ergebnis.push(knoten);
  });
  return ergebnis;
}

/** Teilt einen Inline-Inhalt am Offset in zwei Hälften (Textläufe werden geteilt, Atome nicht). */
export function inhaltTeilen(inhalt: Inlineknoten[], offset: number): [Inlineknoten[], Inlineknoten[]] {
  const links: Inlineknoten[] = [];
  const rechts: Inlineknoten[] = [];
  let anfang = 0;
  inhalt.forEach((knoten) => {
    const laenge = knotenLaenge(knoten);
    const ende = anfang + laenge;
    if (ende <= offset) links.push(knoten);
    else if (anfang >= offset) rechts.push(knoten);
    else if (knoten.typ === 'text') {
      links.push(textlauf(knoten.text.slice(0, offset - anfang), formatVon(knoten)));
      rechts.push(textlauf(knoten.text.slice(offset - anfang), formatVon(knoten)));
    } else {
      // Ein Atom kann nicht geteilt werden; ein Offset „mittendrin“ kommt nicht vor (Länge 1).
      rechts.push(knoten);
    }
    anfang = ende;
  });
  return [normalisiereInhalt(links), normalisiereInhalt(rechts)];
}

function inhaltEinfuegen(inhalt: Inlineknoten[], offset: number, knoten: Inlineknoten[]): Inlineknoten[] {
  const [links, rechts] = inhaltTeilen(inhalt, offset);
  return normalisiereInhalt([...links, ...knoten, ...rechts]);
}

/** Einheiten der Löscheinheit vor dem Offset (Zeichen bzw. Graphemcluster, Atom = 1); 0 am Anfang. */
function einheitVor(inhalt: Inlineknoten[], offset: number): number {
  if (offset <= 0) return 0;
  const stelle = stelleAnOffset(inhalt, offset);
  const knoten = inhalt[stelle.index];
  if (!knoten || knoten.typ !== 'text') return 1;
  return graphemVor(knoten.text, stelle.innen);
}

/** Einheiten der Löscheinheit nach dem Offset; 0 am Ende. */
function einheitNach(inhalt: Inlineknoten[], offset: number): number {
  if (offset >= inhaltLaenge(inhalt)) return 0;
  let anfang = 0;
  for (let index = 0; index < inhalt.length; index += 1) {
    const knoten = inhalt[index];
    const laenge = knotenLaenge(knoten);
    if (offset < anfang + laenge) return knoten.typ === 'text' ? graphemNach(knoten.text, offset - anfang) : 1;
    anfang += laenge;
  }
  return 1;
}

// ---------------------------------------------------------------------------------------------
// Blöcke und Normalform

function blockMitInhalt(block: Block, eintrag: number, inhalt: Inlineknoten[]): Block {
  if (block.typ === 'absatz') return { ...block, inhalt };
  if (block.typ === 'liste') {
    const eintraege = block.eintraege.slice();
    eintraege[eintrag] = inhalt;
    return { ...block, eintraege };
  }
  return block;
}

function inhalteGleich(a: Inlineknoten[], b: Inlineknoten[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((knoten, index) => {
    const anderer = b[index];
    if (knoten.typ !== anderer.typ) return false;
    if (knoten.typ === 'text' && anderer.typ === 'text') {
      return knoten.text === anderer.text && formateGleich(formatVon(knoten), formatVon(anderer));
    }
    if (knoten.typ === 'platzhalter' && anderer.typ === 'platzhalter') {
      return knoten.schluessel === anderer.schluessel && formateGleich(formatVon(knoten), formatVon(anderer));
    }
    if (knoten.typ === 'kaestchen' && anderer.typ === 'kaestchen') return knoten.angekreuzt === anderer.angekreuzt;
    return true;
  });
}

/** Normalisiert einen Block; ein unveränderter Block bleibt dasselbe Objekt. */
function normalisiereBlock(block: Block): Block | null {
  if (block.typ === 'absatz') {
    const inhalt = normalisiereInhalt(block.inhalt);
    return inhalteGleich(inhalt, block.inhalt) ? block : { ...block, inhalt };
  }
  if (block.typ === 'liste') {
    if (block.eintraege.length === 0) return null;
    const eintraege = block.eintraege.map(normalisiereInhalt);
    const gleich = eintraege.every((eintrag, index) => inhalteGleich(eintrag, block.eintraege[index]));
    return gleich ? block : { ...block, eintraege };
  }
  return block;
}

interface Listenzuordnung {
  id: string;
  versatz: number;
}

/**
 * Normalform der Blöcke eines Bereichs. Liefert zusätzlich, welche Listen in eine vorhergehende Liste
 * aufgegangen sind (Kennung → Zielkennung und Eintragsversatz), damit Positionen übertragen werden.
 */
function normalisiereBloecke(
  bloecke: Block[],
  bereich: Bereich,
): { bloecke: Block[]; zuordnung: Map<string, Listenzuordnung> } {
  const ergebnis: Block[] = [];
  const zuordnung = new Map<string, Listenzuordnung>();
  let geaendert = false;
  bloecke.forEach((block) => {
    const normalisiert = normalisiereBlock(block);
    if (!normalisiert) {
      geaendert = true;
      return;
    }
    if (normalisiert !== block) geaendert = true;
    const letzter = ergebnis[ergebnis.length - 1];
    if (normalisiert.typ === 'seitenumbruch') {
      // Ein Umbruch am Textanfang, in Kopf/Fuß oder direkt nach einem Umbruch hat keine Wirkung.
      if (bereich !== 'text' || !letzter || letzter.typ === 'seitenumbruch') {
        geaendert = true;
        return;
      }
    }
    if (normalisiert.typ === 'liste' && letzter && letzter.typ === 'liste') {
      zuordnung.set(normalisiert.id, { id: letzter.id, versatz: letzter.eintraege.length });
      ergebnis[ergebnis.length - 1] = { ...letzter, eintraege: [...letzter.eintraege, ...normalisiert.eintraege] };
      geaendert = true;
      return;
    }
    ergebnis.push(normalisiert);
  });
  if (bereich === 'text' && ergebnis.length === 0) return { bloecke: [absatz([], 'b1')], zuordnung };
  // Kopf-/Fußzeile behalten während der Bearbeitung ihren (leeren) Absatz, damit die Auswahl gültig
  // bleibt; zu „kein Inhalt“ ([]) werden sie erst beim Verlassen des Bereichs bzw. beim Laden
  // (leerenBereichAufraeumen, normalisiereDokument) – KI-Review !750.
  return { bloecke: geaendert ? ergebnis : bloecke, zuordnung };
}

/** Bringt ein geladenes Dokument in die Normalform (ohne Verlauf); unverändert bleibt dasselbe Objekt. */
/** Kopf-/Fußbereich ohne sichtbaren Inhalt: nur leere Absätze und Listen, deren Einträge alle leer sind. */
function ohneSichtbarenInhalt(bloecke: Block[]): boolean {
  return bloecke.every((block) => {
    if (block.typ === 'absatz') return block.inhalt.length === 0;
    if (block.typ === 'liste') return block.eintraege.every((eintrag) => eintrag.length === 0);
    return true;
  });
}

export function normalisiereDokument(dokument: Vertragsdokument): Vertragsdokument {
  let ergebnis = dokument;
  ALLE_BEREICHE.forEach((bereich) => {
    const vorher = bloeckeVon(ergebnis, bereich);
    let { bloecke } = normalisiereBloecke(vorher, bereich);
    // Beim Laden: Kopf/Fuß ohne sichtbaren Inhalt ist „kein Inhalt“ ([]).
    if (bereich !== 'text' && bloecke.length > 0 && ohneSichtbarenInhalt(bloecke)) bloecke = [];
    if (bloecke !== vorher) ergebnis = mitBloecken(ergebnis, bereich, bloecke);
  });
  return ergebnis;
}

/** Schließt eine Operation ab: Normalform herstellen, Auswahl auf verschmolzene Listen übertragen, klemmen. */
function fertig(
  dokument: Vertragsdokument,
  bereich: Bereich,
  bloecke: Block[],
  auswahl: Auswahl,
): Bearbeitungsergebnis {
  const { bloecke: normalisiert, zuordnung } = normalisiereBloecke(bloecke, bereich);
  const neu = mitBloecken(dokument, bereich, normalisiert);
  const uebertragen = (stelle: Position): Position => {
    const ziel = stelle.bereich === bereich ? zuordnung.get(stelle.blockId) : undefined;
    return ziel ? position(bereich, ziel.id, stelle.offset, stelle.eintrag + ziel.versatz) : stelle;
  };
  const angepasst = { anker: uebertragen(auswahl.anker), fokus: uebertragen(auswahl.fokus) };
  const geklemmteAuswahl = auswahlKlemmen(neu, angepasst);
  if (geklemmteAuswahl) return { dokument: neu, auswahl: geklemmteAuswahl };
  // Kommt nur vor, wenn der Bereich keinen Block mehr hat; dann gilt der Anfang des Textes.
  const anfang = anfangVon(neu, bereich) ?? anfangVon(neu, 'text');
  return { dokument: neu, auswahl: anfang ? kollabiert(anfang) : angepasst };
}

function geklemmt(dokument: Vertragsdokument, auswahl: Auswahl): Auswahl | null {
  return auswahlKlemmen(dokument, auswahl);
}

function unveraendert(dokument: Vertragsdokument, auswahl: Auswahl): Bearbeitungsergebnis {
  return { dokument, auswahl };
}

function blockAn(dokument: Vertragsdokument, stelle: Position): Block | undefined {
  return bloeckeVon(dokument, stelle.bereich).find((block) => block.id === stelle.blockId);
}

/**
 * Löscht den Bereich zwischen zwei sortierten Positionen desselben Bereichs. Der Startblock bestimmt
 * Typ und Kennung; der Rest hinter dem Ende wird an die Startstelle angehängt, Blöcke dazwischen
 * (auch Seitenumbrüche) entfallen. Endet die Auswahl in einer Liste, bleiben deren übrige Einträge
 * erhalten (in der Startliste bzw. als Liste hinter dem Startabsatz). Die Auswahl kollabiert am Anfang.
 */
function bereichLoeschen(dokument: Vertragsdokument, start: Position, ende: Position): Bearbeitungsergebnis {
  const { bereich } = start;
  const bloecke = bloeckeVon(dokument, bereich);
  const iStart = bloecke.findIndex((block) => block.id === start.blockId);
  const iEnde = bloecke.findIndex((block) => block.id === ende.blockId);
  if (iStart < 0 || iEnde < 0) return unveraendert(dokument, kollabiert(start));
  const startBlock = bloecke[iStart];
  const endeBlock = bloecke[iEnde];
  const startInhalt = inhaltVon(startBlock, start.eintrag) ?? [];
  const endeInhalt = inhaltVon(endeBlock, ende.eintrag) ?? [];
  const [links] = inhaltTeilen(startInhalt, start.offset);
  const [, rechts] = inhaltTeilen(endeInhalt, ende.offset);
  const verbunden = normalisiereInhalt([...links, ...rechts]);
  const neu: Block[] = [];

  if (iStart === iEnde) {
    if (startBlock.typ === 'liste' && start.eintrag !== ende.eintrag) {
      const eintraege = [
        ...startBlock.eintraege.slice(0, start.eintrag),
        verbunden,
        ...startBlock.eintraege.slice(ende.eintrag + 1),
      ];
      neu.push({ ...startBlock, eintraege });
    } else {
      neu.push(blockMitInhalt(startBlock, start.eintrag, verbunden));
    }
  } else if (startBlock.typ === 'liste') {
    const eintraege = [...startBlock.eintraege.slice(0, start.eintrag), verbunden];
    if (endeBlock.typ === 'liste') eintraege.push(...endeBlock.eintraege.slice(ende.eintrag + 1));
    neu.push({ ...startBlock, eintraege });
  } else {
    neu.push(blockMitInhalt(startBlock, 0, verbunden));
    if (endeBlock.typ === 'liste') {
      const rest = endeBlock.eintraege.slice(ende.eintrag + 1);
      if (rest.length > 0) neu.push({ ...endeBlock, eintraege: rest });
    }
  }
  const ergebnis = [...bloecke.slice(0, iStart), ...neu, ...bloecke.slice(iEnde + 1)];
  return fertig(dokument, bereich, ergebnis, kollabiert(start));
}

// ---------------------------------------------------------------------------------------------
// Öffentliche Operationen

/**
 * Löscht die Auswahl (falls nicht kollabiert) und fügt die Knoten an ihrem Anfang ein. Werden Knoten
 * eingefügt, gilt wie beim Tippen die ent-hängte Auswahl (Dreifachklick zieht den Folgeblock nicht
 * hinein); reines Löschen (keine Knoten) verbindet die Blöcke wie im Browser.
 */
export function ersetzeAuswahl(
  dokument: Vertragsdokument,
  auswahl: Auswahl,
  knoten: Inlineknoten[],
): Bearbeitungsergebnis {
  const geklemmteAuswahl = geklemmt(dokument, auswahl);
  if (!geklemmteAuswahl) return unveraendert(dokument, auswahl);
  const sicher = knoten.length > 0 ? auswahlEntHaengen(dokument, geklemmteAuswahl) : geklemmteAuswahl;
  const [start, ende] = auswahlSortiert(dokument, sicher);
  let aktuell: Bearbeitungsergebnis = { dokument, auswahl: kollabiert(start) };
  if (!istKollabiert(sicher)) aktuell = bereichLoeschen(dokument, start, ende);
  if (knoten.length === 0) return aktuell;
  const stelle = aktuell.auswahl.fokus;
  const bloecke = bloeckeVon(aktuell.dokument, stelle.bereich);
  const index = bloecke.findIndex((block) => block.id === stelle.blockId);
  if (index < 0) return aktuell;
  const block = bloecke[index];
  const inhalt = inhaltVon(block, stelle.eintrag);
  if (!inhalt) return aktuell;
  const neueBloecke = bloecke.slice();
  neueBloecke[index] = blockMitInhalt(block, stelle.eintrag, inhaltEinfuegen(inhalt, stelle.offset, knoten));
  const danach = position(stelle.bereich, stelle.blockId, stelle.offset + inhaltLaenge(knoten), stelle.eintrag);
  return fertig(aktuell.dokument, stelle.bereich, neueBloecke, kollabiert(danach));
}

function formatAmAuswahlanfang(dokument: Vertragsdokument, entHaengt: Auswahl): Formatmerkmale {
  const [start] = auswahlSortiert(dokument, entHaengt);
  const block = blockAn(dokument, start);
  const inhalt = block ? inhaltVon(block, start.eintrag) : null;
  return inhalt ? formatAnStelle(inhalt, start.offset) : OHNE_FORMAT;
}

/**
 * Format, das eingefügter Text ohne angegebenes Format erbt: das Format am Anfang der Auswahl, bevor
 * sie ersetzt wird (dieselbe Regel wie in `textEinfuegen`).
 */
export function einfuegeformat(dokument: Vertragsdokument, auswahl: Auswahl): Formatmerkmale {
  const sicher = geklemmt(dokument, auswahl);
  return sicher ? formatAmAuswahlanfang(dokument, auswahlEntHaengen(dokument, sicher)) : OHNE_FORMAT;
}

/**
 * Ersetzt die Auswahl durch Inline-Knoten nach denselben Auswahlregeln wie `textEinfuegen`: Die
 * Auswahl wird vorher ent-hängt und von `ersetzeAuswahl` noch einmal; eine leere Zeile vor dem
 * Folgeblock bleibt dadurch stehen. Für Platzhalter aus der Zwischenablage (Abschlussreview !756) und
 * seit RITA-1533 (V11) für alle Einfügewege: Platzhalterleiste, Kästchen-Knopf und Umschalt+Enter.
 */
export function knotenWieTextEinfuegen(
  dokument: Vertragsdokument,
  auswahl: Auswahl,
  knoten: Inlineknoten[],
): Bearbeitungsergebnis {
  const sicher = geklemmt(dokument, auswahl);
  if (!sicher) return unveraendert(dokument, auswahl);
  return ersetzeAuswahl(dokument, auswahlEntHaengen(dokument, sicher), knoten);
}

/**
 * Fügt Text ein; ohne angegebenes Format erbt er das Format an der Einfügestelle. Eine „hängende“
 * Auswahl (Dreifachklick: Ende bei Offset 0 des Folgeblocks) ersetzt nur den markierten Block.
 */
export function textEinfuegen(
  dokument: Vertragsdokument,
  auswahl: Auswahl,
  text: string,
  format?: Formatmerkmale,
): Bearbeitungsergebnis {
  const sicher = geklemmt(dokument, auswahl);
  if (!sicher) return unveraendert(dokument, auswahl);
  const entHaengt = auswahlEntHaengen(dokument, sicher);
  if (text === '') return ersetzeAuswahl(dokument, entHaengt, []);
  return ersetzeAuswahl(dokument, entHaengt, [textlauf(text, format ?? formatAmAuswahlanfang(dokument, entHaengt))]);
}

/**
 * Platzhalter erhalten beim Einfügen ein Format wie getippter Text (RITA-1533): das ausstehende
 * Format bzw. das Format der Einfügestelle, das der Aufrufer VOR dem Ersetzen ermittelt
 * (`einfuegeformat`). Kästchen und Zeilenumbrüche tragen kein Format und bleiben unverändert.
 */
export function mitEinfuegeformat(knoten: Inlineknoten[], format: Formatmerkmale): Inlineknoten[] {
  return knoten.map((einzeln) =>
    einzeln.typ === 'platzhalter' ? platzhalterknoten(einzeln.schluessel, format) : einzeln,
  );
}

/**
 * Die Auswahl, die eine Einfügung tatsächlich ersetzt (V11, RITA-1533): zweimal ent-hängt wie in
 * `textEinfuegen` und `knotenWieTextEinfuegen` (einmal vorab, einmal in `ersetzeAuswahl`). Für Wege,
 * die erst löschen und später einfügen, etwa den Beginn einer Sonderzeicheneingabe (´ + e).
 */
export function einfuegeauswahl(dokument: Vertragsdokument, auswahl: Auswahl): Auswahl {
  const sicher = geklemmt(dokument, auswahl);
  return sicher ? auswahlEntHaengen(dokument, auswahlEntHaengen(dokument, sicher)) : auswahl;
}

/** Umschalt+Enter: ein Zeilenumbruch, mit denselben Auswahlregeln wie Tippen (V11, RITA-1533). */
export function zeilenumbruchEinfuegen(dokument: Vertragsdokument, auswahl: Auswahl): Bearbeitungsergebnis {
  return knotenWieTextEinfuegen(dokument, auswahl, [zeilenumbruch()]);
}

export function loescheAuswahl(dokument: Vertragsdokument, auswahl: Auswahl): Bearbeitungsergebnis {
  return ersetzeAuswahl(dokument, auswahl, []);
}

/**
 * Backspace. Offset > 0: eine Einheit vor dem Caret. Offset 0, in dieser Reihenfolge: Listeneintrag
 * k > 0 → mit Eintrag k−1 verbinden; erster Listeneintrag → wird Absatz vor der Liste (auch am
 * Textanfang und nach einem Umbruch); Absatz nach Seitenumbruch → nur den Umbruch entfernen; Absatz
 * nach Absatz oder Liste → an den Vorgänger (bzw. dessen letzten Eintrag) anhängen; Bereichsanfang →
 * unverändert.
 */
export function loescheRueckwaerts(dokument: Vertragsdokument, auswahl: Auswahl): Bearbeitungsergebnis {
  const sicher = geklemmt(dokument, auswahl);
  if (!sicher) return unveraendert(dokument, auswahl);
  if (!istKollabiert(sicher)) return loescheAuswahl(dokument, sicher);
  const stelle = sicher.fokus;
  const { bereich } = stelle;
  const bloecke = bloeckeVon(dokument, bereich);
  const index = bloecke.findIndex((block) => block.id === stelle.blockId);
  const block = bloecke[index];
  const inhalt = inhaltVon(block, stelle.eintrag) ?? [];

  if (stelle.offset > 0) {
    const laenge = einheitVor(inhalt, stelle.offset);
    return bereichLoeschen(dokument, { ...stelle, offset: stelle.offset - laenge }, stelle);
  }
  if (block.typ === 'liste') {
    if (stelle.eintrag > 0) {
      const vorher = position(bereich, block.id, blockLaenge(block, stelle.eintrag - 1), stelle.eintrag - 1);
      return bereichLoeschen(dokument, vorher, stelle);
    }
    const neuerAbsatz = absatz(inhalt, neueKennung(dokument, bereich));
    const rest: Block[] = block.eintraege.length > 1 ? [{ ...block, eintraege: block.eintraege.slice(1) }] : [];
    const neueBloecke = [...bloecke.slice(0, index), neuerAbsatz, ...rest, ...bloecke.slice(index + 1)];
    return fertig(dokument, bereich, neueBloecke, kollabiert(position(bereich, neuerAbsatz.id, 0)));
  }
  if (index === 0) return unveraendert(dokument, sicher);
  const vorgaenger = bloecke[index - 1];
  if (vorgaenger.typ === 'seitenumbruch') {
    const neueBloecke = [...bloecke.slice(0, index - 1), ...bloecke.slice(index)];
    return fertig(dokument, bereich, neueBloecke, sicher);
  }
  const letzterEintrag = anzahlEintraege(vorgaenger) - 1;
  const vorher = position(bereich, vorgaenger.id, blockLaenge(vorgaenger, letzterEintrag), letzterEintrag);
  return bereichLoeschen(dokument, vorher, stelle);
}

/**
 * Entf. Vor dem Blockende: eine Einheit nach dem Caret. Am Ende, in dieser Reihenfolge: Listeneintrag
 * k < letzter → Eintrag k+1 anhängen; nächster Block ist Seitenumbruch → entfernen; nächster Block
 * ist Absatz → dessen Inhalt anhängen; nächster Block ist Liste → ihren ersten Eintrag anhängen (eine
 * leere Liste entfällt); Bereichsende → unverändert.
 */
export function loescheVorwaerts(dokument: Vertragsdokument, auswahl: Auswahl): Bearbeitungsergebnis {
  const sicher = geklemmt(dokument, auswahl);
  if (!sicher) return unveraendert(dokument, auswahl);
  if (!istKollabiert(sicher)) return loescheAuswahl(dokument, sicher);
  const stelle = sicher.fokus;
  const { bereich } = stelle;
  const bloecke = bloeckeVon(dokument, bereich);
  const index = bloecke.findIndex((block) => block.id === stelle.blockId);
  const block = bloecke[index];
  const inhalt = inhaltVon(block, stelle.eintrag) ?? [];

  const laenge = einheitNach(inhalt, stelle.offset);
  if (laenge > 0) return bereichLoeschen(dokument, stelle, { ...stelle, offset: stelle.offset + laenge });
  if (block.typ === 'liste' && stelle.eintrag < block.eintraege.length - 1) {
    return bereichLoeschen(dokument, stelle, position(bereich, block.id, 0, stelle.eintrag + 1));
  }
  const nachfolger = bloecke[index + 1];
  if (!nachfolger) return unveraendert(dokument, sicher);
  if (nachfolger.typ === 'seitenumbruch') {
    const neueBloecke = [...bloecke.slice(0, index + 1), ...bloecke.slice(index + 2)];
    return fertig(dokument, bereich, neueBloecke, sicher);
  }
  return bereichLoeschen(dokument, stelle, position(bereich, nachfolger.id, 0));
}

/**
 * Enter: löscht zuerst eine Auswahl und teilt dann den Block am Caret; der erste Teil behält die
 * Kennung. Ein leerer Listeneintrag wird zum Absatz: am Ende verlässt er die Liste, in der Mitte
 * teilt er sie (zweiter Teil mit neuer Kennung).
 */
export function blockTeilen(dokument: Vertragsdokument, auswahl: Auswahl): Bearbeitungsergebnis {
  const sicher = geklemmt(dokument, auswahl);
  if (!sicher) return unveraendert(dokument, auswahl);
  // Chromium hängt einen Dreifachklick an den Anfang des Folgeblocks. Enter ersetzt nur den
  // markierten Inhalt; der unmarkierte Folgeblock behält wie beim Tippen Kennung, Art und Inhalt.
  const wirksam = auswahlEntHaengen(dokument, sicher);
  const start = istKollabiert(wirksam) ? { dokument, auswahl: wirksam } : loescheAuswahl(dokument, wirksam);
  const stelle = start.auswahl.fokus;
  const { bereich } = stelle;
  const bloecke = bloeckeVon(start.dokument, bereich);
  const index = bloecke.findIndex((block) => block.id === stelle.blockId);
  const block = bloecke[index];
  const inhalt = inhaltVon(block, stelle.eintrag);
  if (!inhalt) return start;
  const [links, rechts] = inhaltTeilen(inhalt, stelle.offset);
  const naechsteKennung = kennungsquelle(start.dokument, bereich);

  if (block.typ === 'liste') {
    if (inhalt.length === 0) {
      const davor = block.eintraege.slice(0, stelle.eintrag);
      const danach = block.eintraege.slice(stelle.eintrag + 1);
      const neuerAbsatz = absatz([], davor.length > 0 ? naechsteKennung() : block.id);
      const teile: Block[] = [];
      if (davor.length > 0) teile.push({ ...block, eintraege: davor });
      teile.push(neuerAbsatz);
      if (danach.length > 0) teile.push(neueListe(danach, naechsteKennung()));
      const neueBloecke = [...bloecke.slice(0, index), ...teile, ...bloecke.slice(index + 1)];
      return fertig(start.dokument, bereich, neueBloecke, kollabiert(position(bereich, neuerAbsatz.id, 0)));
    }
    const eintraege = [
      ...block.eintraege.slice(0, stelle.eintrag),
      links,
      rechts,
      ...block.eintraege.slice(stelle.eintrag + 1),
    ];
    const neueBloecke = bloecke.slice();
    neueBloecke[index] = { ...block, eintraege };
    return fertig(start.dokument, bereich, neueBloecke, kollabiert(position(bereich, block.id, 0, stelle.eintrag + 1)));
  }
  const kennung = naechsteKennung();
  const neuerAbsatz = absatz(rechts, kennung);
  const neueBloecke = [
    ...bloecke.slice(0, index),
    { ...block, inhalt: links },
    neuerAbsatz,
    ...bloecke.slice(index + 1),
  ];
  return fertig(start.dokument, bereich, neueBloecke, kollabiert(position(bereich, kennung, 0)));
}

// ---------------------------------------------------------------------------------------------
// Format

interface Inhaltsabschnitt {
  blockIndex: number;
  eintrag: number;
  von: number;
  bis: number;
}

/** Alle (Block, Eintrag)-Abschnitte einer sortierten Auswahl mit den Offsets innerhalb des Eintrags. */
function abschnitteVon(dokument: Vertragsdokument, start: Position, ende: Position): Inhaltsabschnitt[] {
  const bloecke = bloeckeVon(dokument, start.bereich);
  const iStart = bloecke.findIndex((block) => block.id === start.blockId);
  const iEnde = bloecke.findIndex((block) => block.id === ende.blockId);
  const abschnitte: Inhaltsabschnitt[] = [];
  if (iStart < 0 || iEnde < 0) return abschnitte;
  for (let blockIndex = iStart; blockIndex <= iEnde; blockIndex += 1) {
    const block = bloecke[blockIndex];
    const eintraege = anzahlEintraege(block);
    for (let eintrag = 0; eintrag < eintraege; eintrag += 1) {
      const vorStart = blockIndex === iStart && eintrag < start.eintrag;
      const nachEnde = blockIndex === iEnde && eintrag > ende.eintrag;
      if (!vorStart && !nachEnde) {
        const laenge = blockLaenge(block, eintrag);
        const von = blockIndex === iStart && eintrag === start.eintrag ? start.offset : 0;
        const bis = blockIndex === iEnde && eintrag === ende.eintrag ? ende.offset : laenge;
        abschnitte.push({ blockIndex, eintrag, von, bis });
      }
    }
  }
  return abschnitte;
}

/** Formatträger einer sortierten Auswahl: Textläufe mit mindestens einem markierten Zeichen und markierte Platzhalter. */
function formattraegerIn(dokument: Vertragsdokument, start: Position, ende: Position): Formattraeger[] {
  const bloecke = bloeckeVon(dokument, start.bereich);
  const laeufe: Formattraeger[] = [];
  abschnitteVon(dokument, start, ende).forEach(({ blockIndex, eintrag, von, bis }) => {
    const inhalt = inhaltVon(bloecke[blockIndex], eintrag) ?? [];
    let anfang = 0;
    inhalt.forEach((knoten) => {
      const laenge = knotenLaenge(knoten);
      if (istFormattraeger(knoten) && anfang < bis && anfang + laenge > von) laeufe.push(knoten);
      anfang += laenge;
    });
  });
  return laeufe;
}

/**
 * Format der Auswahl. Kollabiert: das Format, das eine Eingabe erben würde. Bereich: nur gesetzt,
 * wenn alle markierten Formatträger es tragen (Textläufe mit mindestens einem markierten Zeichen und
 * markierte Platzhalter, RITA-1533); ohne Formatträger (nur Kästchen markiert) ist nichts gesetzt.
 * Die eigene Größe (RITA-1534) steht nur, wenn alle markierten Formatträger dieselbe eigene Größe
 * tragen; die wirksame Größe einer Markierung liefert `groesseAnAuswahl`.
 */
export function formatAnAuswahl(dokument: Vertragsdokument, auswahl: Auswahl): Formatmerkmale {
  const sicher = geklemmt(dokument, auswahl);
  if (!sicher) return OHNE_FORMAT;
  if (istKollabiert(sicher)) {
    const block = blockAn(dokument, sicher.fokus);
    const inhalt = block ? inhaltVon(block, sicher.fokus.eintrag) : null;
    return inhalt ? formatAnStelle(inhalt, sicher.fokus.offset) : OHNE_FORMAT;
  }
  const [start, ende] = auswahlSortiert(dokument, auswahlEntHaengen(dokument, sicher));
  const laeufe = formattraegerIn(dokument, start, ende);
  if (laeufe.length === 0) return OHNE_FORMAT;
  const groessen = new Set(laeufe.map((lauf) => lauf.groessePt));
  return mitGroesse(
    {
      fett: laeufe.every((lauf) => !!lauf.fett),
      kursiv: laeufe.every((lauf) => !!lauf.kursiv),
      unterstrichen: laeufe.every((lauf) => !!lauf.unterstrichen),
    },
    groessen.size === 1 ? laeufe[0].groessePt : undefined,
  );
}

/**
 * Wirksame Schriftgröße der Auswahl in Punkt (RITA-1534): an der Schreibmarke die Größe, die eine Eingabe
 * erben würde; bei einer Markierung die gemeinsame wirksame Größe aller markierten Formatträger (eigene
 * Größe oder Grundgröße des Bereichs). `null`, wenn verschieden große Formatträger markiert sind („–“ im
 * Auswahlfeld). Ohne Formatträger (nur Kästchen markiert) gilt die Grundgröße des Bereichs.
 */
export function groesseAnAuswahl(dokument: Vertragsdokument, auswahl: Auswahl): number | null {
  const sicher = geklemmt(dokument, auswahl);
  const grundgroesse = grundgroesseVon(dokument, (sicher ?? auswahl).anker.bereich);
  if (!sicher) return grundgroesse;
  if (istKollabiert(sicher)) return formatAnAuswahl(dokument, sicher).groessePt ?? grundgroesse;
  const [start, ende] = auswahlSortiert(dokument, auswahlEntHaengen(dokument, sicher));
  const groessen = new Set(formattraegerIn(dokument, start, ende).map((lauf) => lauf.groessePt ?? grundgroesse));
  if (groessen.size > 1) return null;
  return groessen.size === 1 ? Array.from(groessen)[0] : grundgroesse;
}

/**
 * Wendet `umformen` auf jeden markierten Formatträger an (Textläufe werden an den Rändern der Markierung
 * geteilt, Platzhalter wechseln ganz; Kästchen und Zeilenumbrüche bleiben). `null` heißt: Knoten
 * unverändert. Ohne Änderung bleibt es dasselbe Dokumentobjekt.
 */
function markierteFormattraegerUmformen(
  dokument: Vertragsdokument,
  sicher: Auswahl,
  umformen: (knoten: Formattraeger) => Formattraeger | null,
): Bearbeitungsergebnis {
  const [start, ende] = auswahlSortiert(dokument, auswahlEntHaengen(dokument, sicher));
  const bloecke = bloeckeVon(dokument, start.bereich).slice();
  let geaendert = false;
  abschnitteVon(dokument, start, ende).forEach(({ blockIndex, eintrag, von, bis }) => {
    const block = bloecke[blockIndex];
    const inhalt = inhaltVon(block, eintrag) ?? [];
    const [links, rest] = inhaltTeilen(inhalt, von);
    const [mitte, rechts] = inhaltTeilen(rest, bis - von);
    const formatiert = mitte.map((knoten) => {
      const neu = istFormattraeger(knoten) ? umformen(knoten) : null;
      if (!neu) return knoten;
      geaendert = true;
      return neu;
    });
    bloecke[blockIndex] = blockMitInhalt(block, eintrag, normalisiereInhalt([...links, ...formatiert, ...rechts]));
  });
  if (!geaendert) return unveraendert(dokument, sicher);
  return fertig(dokument, start.bereich, bloecke, sicher);
}

/** Setzt ein Merkmal auf der Auswahl, wenn es nicht überall gesetzt ist, sonst entfernt es es. Kollabiert: unverändert. */
export function formatUmschalten(
  dokument: Vertragsdokument,
  auswahl: Auswahl,
  merkmal: Formatmerkmal,
): Bearbeitungsergebnis {
  const sicher = geklemmt(dokument, auswahl);
  if (!sicher || istKollabiert(sicher)) return unveraendert(dokument, sicher ?? auswahl);
  const setzen = !formatAnAuswahl(dokument, sicher)[merkmal];
  return markierteFormattraegerUmformen(dokument, sicher, (knoten) =>
    !!knoten[merkmal] === setzen ? null : mitFormat(knoten, { ...formatVon(knoten), [merkmal]: setzen }),
  );
}

/**
 * Setzt die Schriftgröße auf der Markierung (RITA-1534). Die Grundgröße des Bereichs wird nicht als eigene
 * Größe gespeichert, sondern entfernt sie (`eigeneGroesse`): Der Text folgt dann wieder der Grundgröße.
 * Kollabiert: unverändert (für die nächste Eingabe merkt sich der Editor ein ausstehendes Format).
 */
export function groesseSetzen(dokument: Vertragsdokument, auswahl: Auswahl, groessePt: number): Bearbeitungsergebnis {
  const sicher = geklemmt(dokument, auswahl);
  if (!sicher || istKollabiert(sicher)) return unveraendert(dokument, sicher ?? auswahl);
  const eigene = eigeneGroesse(dokument, sicher.anker.bereich, groessePt);
  return markierteFormattraegerUmformen(dokument, sicher, (knoten) =>
    knoten.groessePt === eigene ? null : mitFormat(knoten, mitGroesse(formatVon(knoten), eigene)),
  );
}

// ---------------------------------------------------------------------------------------------
// Aufzählung

/**
 * Wirkt je Eintrag: Sind alle ausgewählten Einträge Listeneinträge, werden genau diese zu Absätzen
 * (Listen davor/danach bleiben, der Rest erhält eine neue Kennung); sonst werden die Absätze der
 * Auswahl zu Listeneinträgen. Direkt angrenzende Listen (auch außerhalb der Auswahl) verschmelzen
 * in der Normalform; ein Seitenumbruch trennt immer. Die Auswahl wird mit übertragen.
 */
export function listeUmschalten(dokument: Vertragsdokument, auswahl: Auswahl): Bearbeitungsergebnis {
  const sicher = geklemmt(dokument, auswahl);
  if (!sicher) return unveraendert(dokument, auswahl);
  const [start, ende] = auswahlSortiert(dokument, auswahlEntHaengen(dokument, sicher));
  const { bereich } = start;
  const bloecke = bloeckeVon(dokument, bereich);
  const iStart = bloecke.findIndex((block) => block.id === start.blockId);
  const iEnde = bloecke.findIndex((block) => block.id === ende.blockId);
  if (iStart < 0 || iEnde < 0) return unveraendert(dokument, sicher);
  const betroffen = bloecke.slice(iStart, iEnde + 1).filter((block) => block.typ !== 'seitenumbruch');
  if (betroffen.length === 0) return unveraendert(dokument, sicher);
  const naechsteKennung = kennungsquelle(dokument, bereich);
  const zuordnung = new Map<string, { id: string; eintrag: number }>();
  const merke = (altId: string, altEintrag: number, neuId: string, neuEintrag: number) =>
    zuordnung.set(`${altId}/${altEintrag}`, { id: neuId, eintrag: neuEintrag });
  const neu: Block[] = [];

  if (betroffen.every((block) => block.typ === 'liste')) {
    bloecke.slice(iStart, iEnde + 1).forEach((block) => {
      if (block.typ !== 'liste') {
        neu.push(block);
        return;
      }
      const von = block.id === start.blockId ? start.eintrag : 0;
      const bis = block.id === ende.blockId ? ende.eintrag : block.eintraege.length - 1;
      const davor = block.eintraege.slice(0, von);
      const danach = block.eintraege.slice(bis + 1);
      if (davor.length > 0) neu.push({ ...block, eintraege: davor });
      block.eintraege.slice(von, bis + 1).forEach((eintrag, lauf) => {
        const id = davor.length === 0 && lauf === 0 ? block.id : naechsteKennung();
        merke(block.id, von + lauf, id, 0);
        neu.push(absatz(eintrag, id));
      });
      if (danach.length > 0) {
        const id = naechsteKennung();
        danach.forEach((_, lauf) => merke(block.id, bis + 1 + lauf, id, lauf));
        neu.push(neueListe(danach, id));
      }
    });
  } else {
    let laufende: Liste | null = null;
    bloecke.slice(iStart, iEnde + 1).forEach((block) => {
      if (block.typ === 'seitenumbruch') {
        laufende = null;
        neu.push(block);
        return;
      }
      if (!laufende) {
        laufende = neueListe([], block.id);
        neu.push(laufende);
      }
      const ziel: Liste = laufende;
      if (block.typ === 'absatz') {
        merke(block.id, 0, ziel.id, ziel.eintraege.length);
        ziel.eintraege.push(block.inhalt);
      } else {
        block.eintraege.forEach((eintrag, lauf) => {
          merke(block.id, lauf, ziel.id, ziel.eintraege.length);
          ziel.eintraege.push(eintrag);
        });
      }
    });
  }
  const neueBloecke = [...bloecke.slice(0, iStart), ...neu, ...bloecke.slice(iEnde + 1)];
  const uebertragen = (stelle: Position): Position => {
    const ziel = zuordnung.get(`${stelle.blockId}/${stelle.eintrag}`);
    return ziel ? position(bereich, ziel.id, stelle.offset, ziel.eintrag) : stelle;
  };
  return fertig(dokument, bereich, neueBloecke, {
    anker: uebertragen(sicher.anker),
    fokus: uebertragen(sicher.fokus),
  });
}

// ---------------------------------------------------------------------------------------------
// Seitenumbruch und Kästchen

/**
 * Die Auswahl reicht vom Ende eines Blocks (Ende seines letzten Eintrags) bis zum Anfang des nächsten
 * Inhaltsblocks (Offset 0 seines ersten Eintrags), und dazwischen stehen nur Seitenumbrüche: Markiert
 * ist dann allein der vorhandene Umbruch (Umschalt+Pfeil über die Seitengrenze, Maus über die Linie).
 */
function nurUmbruecheMarkiert(dokument: Vertragsdokument, auswahl: Auswahl): boolean {
  if (istKollabiert(auswahl)) return false;
  const [start, ende] = auswahlSortiert(dokument, auswahl);
  const bloecke = bloeckeVon(dokument, start.bereich);
  const iStart = bloecke.findIndex((block) => block.id === start.blockId);
  const iEnde = bloecke.findIndex((block) => block.id === ende.blockId);
  if (iStart < 0 || iEnde <= iStart + 1) return false;
  const letzterEintrag = anzahlEintraege(bloecke[iStart]) - 1;
  if (start.eintrag !== letzterEintrag || start.offset !== blockLaenge(bloecke[iStart], letzterEintrag)) return false;
  if (ende.eintrag !== 0 || ende.offset !== 0) return false;
  return bloecke.slice(iStart + 1, iEnde).every((block) => block.typ === 'seitenumbruch');
}

/**
 * Die Auswahl endet am Anfang (Offset 0 des ersten Eintrags) eines Blocks anderer Art als der Block, in
 * dem sie beginnt (Aufzählung → Absatz oder Absatz → Aufzählung), und direkt vor diesem Block steht
 * kein Seitenumbruch. So markiert ein Dreifachklick in Chromium den letzten Punkt einer Aufzählung vor
 * einem Absatz bzw. einen Absatz vor einer Aufzählung; die Regel gilt aber für jede Auswahl dieser Form,
 * gleich wo sie beginnt. Den Fall mit einem Seitenumbruch direkt davor fängt `seitenumbruchEinfuegen`
 * vorher über `haengtHinterUmbruch` ab; die Bedingung hier hält die Funktion für sich allein richtig.
 */
function endetAmAnfangAndererBlockart(dokument: Vertragsdokument, auswahl: Auswahl): boolean {
  if (istKollabiert(auswahl)) return false;
  const [start, ende] = auswahlSortiert(dokument, auswahl);
  if (ende.offset !== 0 || ende.eintrag !== 0 || ende.blockId === start.blockId) return false;
  const bloecke = bloeckeVon(dokument, start.bereich);
  const iEnde = bloecke.findIndex((block) => block.id === ende.blockId);
  const startBlock = blockAn(dokument, start);
  const endeBlock = bloecke[iEnde];
  const davor = bloecke[iEnde - 1];
  if (!startBlock || !endeBlock || !davor || davor.typ === 'seitenumbruch') return false;
  return startBlock.typ !== endeBlock.typ;
}

/**
 * Die Auswahl endet am Anfang (Offset 0 des ersten Eintrags) eines späteren Blocks, und direkt vor diesem
 * Block steht schon ein Seitenumbruch: Sie „hängt“ über den vorhandenen Umbruch. So markiert ein
 * Dreifachklick in Chromium den letzten Punkt bzw. die letzte Zeile vor einem Umbruch, ebenso
 * Umschalt+Pfeil runter aus einem Absatz; die Regel gilt für jede Auswahl dieser Form, gleich wo sie
 * beginnt und welcher Art die Blöcke sind. `nurUmbruecheMarkiert` ist ein Sonderfall davon.
 */
function haengtHinterUmbruch(dokument: Vertragsdokument, auswahl: Auswahl): boolean {
  if (istKollabiert(auswahl)) return false;
  const [start, ende] = auswahlSortiert(dokument, auswahl);
  if (ende.offset !== 0 || ende.eintrag !== 0 || ende.blockId === start.blockId) return false;
  const bloecke = bloeckeVon(dokument, start.bereich);
  const davor = bloecke[bloecke.findIndex((block) => block.id === ende.blockId) - 1];
  return !!davor && davor.typ === 'seitenumbruch';
}

/** Leerzeile oder Aufzählung mit genau einem leeren Punkt. */
function istLeererEinzelblock(block: Block): boolean {
  return block.typ !== 'seitenumbruch' && anzahlEintraege(block) === 1 && blockLaenge(block, 0) === 0;
}

/**
 * „Seitenumbruch einfügen“ über einer Auswahl, die über einen vorhandenen Umbruch hängt (`haengtHinterUmbruch`):
 * Löscht `ohneUmbruch` (die Auswahl bis zum Ende des Inhaltsblocks vor dem Umbruch). Der Eintrag bzw.
 * Block, der dabei leer wird, entfällt wie bei V1 – außer es steht nichts (oder nur ein Umbruch) davor,
 * dann bleibt er als Leerzeile bzw. leerer Punkt mit der Schreibmarke stehen. Sonst steht die Schreibmarke
 * am Anfang des Folgeblocks hinter dem vorhandenen Umbruch (`folgeAnfang`), wie nach jedem Umbruch am
 * Blockende. Umbruch und Folgeblock bleiben nach Kennung, Art und Inhalt dieselben; ein neuer Umbruch
 * entsteht nicht. `null`, wenn die Stelle nach dem Löschen nicht auffindbar ist (nicht normalisiert).
 */
function markiertesVorUmbruchLoeschen(
  dokument: Vertragsdokument,
  ohneUmbruch: Auswahl,
  folgeAnfang: Position,
): Bearbeitungsergebnis | null {
  const geloescht = loescheAuswahl(dokument, ohneUmbruch);
  const stelle = geloescht.auswahl.fokus;
  const bloecke = bloeckeVon(geloescht.dokument, 'text');
  const index = bloecke.findIndex((block) => block.id === stelle.blockId);
  const block = bloecke[index];
  if (!block || block.typ === 'seitenumbruch') return null;
  const folge = kollabiert(position('text', folgeAnfang.blockId, 0));
  if (blockLaenge(block, stelle.eintrag) > 0) return fertig(geloescht.dokument, 'text', bloecke, folge);
  if (block.typ === 'liste' && block.eintraege.length > 1) {
    const ohneLeeren: Block = { ...block, eintraege: block.eintraege.filter((_eintrag, i) => i !== stelle.eintrag) };
    return fertig(
      geloescht.dokument,
      'text',
      [...bloecke.slice(0, index), ohneLeeren, ...bloecke.slice(index + 1)],
      folge,
    );
  }
  const vorgaenger = bloecke[index - 1];
  if (!vorgaenger || vorgaenger.typ === 'seitenumbruch')
    return fertig(geloescht.dokument, 'text', bloecke, kollabiert(stelle));
  return fertig(geloescht.dokument, 'text', [...bloecke.slice(0, index), ...bloecke.slice(index + 1)], folge);
}

/**
 * Fügt im Text einen manuellen Seitenumbruch am Caret ein. Am Blockanfang kommt er vor den Block
 * (der Caret bleibt), am Blockende dahinter (der Caret wandert an den Anfang des Folgeblocks; fehlt
 * einer, entsteht ein leerer Absatz), sonst wird der Block geteilt. Am Textanfang oder direkt neben
 * einem vorhandenen Umbruch ändert sich nichts.
 *
 * Leere Listeneinträge: Der Umbruch selbst erzeugt keinen. Ein leerer bzw. durch das Löschen der
 * Auswahl leer gewordener Eintrag bleibt nur dort stehen, wo danach der Caret steht – als erster oder
 * einziger Eintrag (der Umbruch kommt vor die Liste, der Caret bleibt im leeren Eintrag). Als letzter
 * von mehreren Einträgen entfällt er: Der Caret wandert hinter den Umbruch, ein Eintrag ohne Text
 * bliebe sonst am Seitenende zurück (RITA-1525 Teil 4c, V1). In der Mitte wird die Liste um ihn geteilt.
 * Das betrifft nur den Eintrag an der Schreibstelle: Schon vorher leere, nicht markierte Einträge
 * bleiben unberührt, auch direkt vor dem neuen Umbruch (Test „V1 bekannte Grenze“).
 *
 * Eine nicht kollabierte Auswahl wird zuerst gelöscht, der Umbruch entsteht an der verbleibenden
 * Stelle. Die Operation ist atomar: Ist der Umbruch dort nicht zulässig, bleiben Dokument und
 * Auswahl unverändert — markierter Text geht nie verloren, ohne dass ein Umbruch entsteht
 * (KI-Review !750 vom 28.09.2026: die Ablehnungspfade gaben vorher das bereits gelöschte
 * Zwischenergebnis zurück).
 *
 * Ist allein ein vorhandener Umbruch markiert (`nurUmbruecheMarkiert`), steht dort schon ein Umbruch:
 * Die Operation wird wie jede andere Ablehnung behandelt – dasselbe Dokument, die Markierung bleibt.
 * Vorher wurde der Umbruch gelöscht und an derselben Stelle mit neuen Kennungen wieder eingefügt;
 * sichtbar änderte sich nichts, es entstand aber ein Verlaufsschritt, und neben einer Aufzählung
 * wechselte der Folgeblock die Art (RITA-1525 Teil 4c, V3). Das gilt auch, wenn direkt am Umbruch
 * eine Leerzeile oder ein leerer Listeneintrag steht.
 *
 * Endet die Auswahl am Anfang eines Blocks anderer Art (`endetAmAnfangAndererBlockart`), gilt die
 * ent-hängte Auswahl: Gelöscht wird nur bis zum Ende des Blocks davor, der Folgeblock bleibt unberührt.
 * Typisch ist das nach einem Dreifachklick; es gilt ebenso für jede andere Auswahl, die dort endet – mit
 * Beginn mitten im Block, über mehrere Blöcke hinweg oder von einer Aufzählung bis an eine Leerzeile
 * (Tests „V1b nicht nur Dreifachklick“). Das rohe Löschen hängte ihn an den Startblock, und der Umbruch
 * gab ihm dessen Art – aus dem Absatz unter der Aufzählung wurde ein Punkt, aus dem ersten Punkt unter
 * einem Absatz ein Absatz (RITA-1525 Teil 4c, V1b). Bewusst eng: Bei gleicher Blockart (Absatz vor
 * Absatz, Punkt vor Punkt) bleibt es beim rohen Löschen. Ausnahme (VGL-1): Ist der Folgeblock leer
 * (Leerzeile oder ein einzelner leerer Punkt) und steht direkt dahinter schon ein Umbruch, käme der neue
 * Umbruch direkt davor – der leere Block stünde allein auf einer Seite. Dann wird abgelehnt, wie vor V1b.
 * Das gilt nur für die ent-hängte Auswahl; die Schreibmarke und eine Auswahl, die schon vor dem Folgeblock
 * endet, bleiben, wie sie sind (Tests „VGL-1 V2 Abgrenzung“).
 *
 * Hängt die Auswahl über einen VORHANDENEN Umbruch (`haengtHinterUmbruch`: Ende am Anfang des Blocks direkt
 * hinter dem Umbruch, gleich welcher Art die Blöcke sind), steht der verlangte Umbruch schon da. Dann wird nur
 * der markierte Inhalt bis zum Ende des Inhaltsblocks vor dem Umbruch gelöscht (`markiertesVorUmbruchLoeschen`):
 * Ein dadurch leer gewordener Eintrag bzw. Block entfällt wie bei V1, der vorhandene Umbruch bleibt, ein
 * neuer entsteht nicht, der Folgeblock bleibt nach Kennung, Art und Inhalt unberührt, und die Schreibmarke
 * steht an seinem Anfang – wie nach jedem Umbruch am Blockende (RITA-1525 Teil 4c, V1b-Rest; Entscheidung
 * Julia 02./03.10.2026: „Der Punkt verschwindet, der Seitenumbruch steht an seiner Stelle, der Absatz
 * darunter bleibt ein normaler Absatz“). Steht vor dem leer gewordenen Block nichts oder nur ein Umbruch,
 * bleibt er als Leerzeile bzw. leerer Punkt mit der Schreibmarke stehen (sonst stünden zwei Umbrüche
 * hintereinander oder der Text begänne mit einem Umbruch). Vorher wurde roh gelöscht: Der Folgeblock wechselte
 * neben einer Aufzählung die Art und bekam auch sonst eine neue Kennung; der vorhandene Umbruch wurde ersetzt.
 * Sichtbar gleich wie vor Teil 4c bleibt: Text samt Umbruch bis an den Anfang des Folgeblocks markiert, löscht
 * den Text, der Umbruch bleibt. Unverändert: Endet die Auswahl MITTEN im Folgeblock oder am Anfang eines
 * späteren Eintrags, wird weiter roh gelöscht (Tests „V1b-Rest A1 Abgrenzung“).
 */
export function seitenumbruchEinfuegen(dokument: Vertragsdokument, auswahl: Auswahl): Bearbeitungsergebnis {
  const sicher = geklemmt(dokument, auswahl);
  if (!sicher || sicher.anker.bereich !== 'text') return unveraendert(dokument, sicher ?? auswahl);
  // `start` ist nur ein Zwischenergebnis: Jede Ablehnung unten gibt das ORIGINAL zurück, nie `start`.
  const abgelehnt = (): Bearbeitungsergebnis => unveraendert(dokument, sicher);
  // Die Reihenfolge der drei Prüfungen ist wichtig: V3 (nur der Umbruch) → V1b-Rest (hängt über einen
  // Umbruch) → V1b (Blockart wechselt). Ohne V3 davor bliebe von „Leerzeile + Umbruch markiert“ nach dem
  // Ent-hängen eine Schreibmarke in der Leerzeile, und davor entstünde ein Umbruch.
  if (nurUmbruecheMarkiert(dokument, sicher)) return abgelehnt();
  if (haengtHinterUmbruch(dokument, sicher)) {
    // Der Umbruch, den die Auswahl verlangt, steht schon da. Gelöscht wird der markierte Inhalt bis zum Ende
    // des Inhaltsblocks vor dem Umbruch; der vorhandene Umbruch bleibt, ein neuer entsteht nicht, und der
    // Folgeblock bleibt nach Kennung, Art und Inhalt unberührt. Gibt das Ent-hängen dieselbe Auswahl
    // zurück (der Block vor dem Umbruch hat kein Ende, nur in nicht normalisierten Dokumenten), wird abgelehnt.
    const ohneUmbruch = auswahlEntHaengen(dokument, sicher);
    if (ohneUmbruch === sicher || istKollabiert(ohneUmbruch)) return abgelehnt();
    const [, folgeAnfang] = auswahlSortiert(dokument, sicher);
    return markiertesVorUmbruchLoeschen(dokument, ohneUmbruch, folgeAnfang) ?? abgelehnt();
  }
  const entHaengt = endetAmAnfangAndererBlockart(dokument, sicher);
  const wirksam = entHaengt ? auswahlEntHaengen(dokument, sicher) : sicher;
  const start = istKollabiert(wirksam) ? { dokument, auswahl: wirksam } : loescheAuswahl(dokument, wirksam);
  const stelle = start.auswahl.fokus;
  const bloecke = bloeckeVon(start.dokument, 'text');
  const index = bloecke.findIndex((block) => block.id === stelle.blockId);
  const block = bloecke[index];
  const inhalt = inhaltVon(block, stelle.eintrag);
  if (!inhalt) return abgelehnt();
  const [links, rechts] = inhaltTeilen(inhalt, stelle.offset);
  const naechsteKennung = kennungsquelle(start.dokument, 'text');

  let vorher: Inlineknoten[][];
  let nachher: Inlineknoten[][];
  if (block.typ === 'liste') {
    vorher = [...block.eintraege.slice(0, stelle.eintrag), ...(links.length > 0 ? [links] : [])];
    nachher = [...(rechts.length > 0 ? [rechts] : []), ...block.eintraege.slice(stelle.eintrag + 1)];
  } else {
    vorher = links.length > 0 ? [links] : [];
    nachher = rechts.length > 0 ? [rechts] : [];
  }

  if (vorher.length === 0) {
    const vorgaenger = bloecke[index - 1];
    if (!vorgaenger || vorgaenger.typ === 'seitenumbruch') return abgelehnt();
    const umbruch = neuerSeitenumbruch(naechsteKennung());
    const neueBloecke = [...bloecke.slice(0, index), umbruch, ...bloecke.slice(index)];
    return fertig(start.dokument, 'text', neueBloecke, kollabiert(stelle));
  }
  if (nachher.length === 0) {
    const nachfolger = bloecke[index + 1];
    if (nachfolger && nachfolger.typ === 'seitenumbruch') return abgelehnt();
    // Ent-hängt, und der unberührte Folgeblock ist leer und steht direkt vor einem vorhandenen Umbruch:
    // Der neue Umbruch ließe ihn allein auf einer Seite zurück. Abgelehnt wie vor V1b (VGL-1).
    const danach = bloecke[index + 2];
    if (entHaengt && nachfolger && istLeererEinzelblock(nachfolger) && danach && danach.typ === 'seitenumbruch') {
      return abgelehnt();
    }
    const umbruch = neuerSeitenumbruch(naechsteKennung());
    // Bei Listen gilt `vorher` (ohne einen leeren letzten Eintrag), nicht der unveränderte Block.
    const davor: Block = block.typ === 'liste' ? { ...block, eintraege: vorher } : block;
    const neueBloecke = [...bloecke.slice(0, index), davor, umbruch, ...bloecke.slice(index + 1)];
    if (nachfolger) return fertig(start.dokument, 'text', neueBloecke, kollabiert(position('text', nachfolger.id, 0)));
    const neuerAbsatz = absatz([], naechsteKennung());
    return fertig(
      start.dokument,
      'text',
      [...neueBloecke, neuerAbsatz],
      kollabiert(position('text', neuerAbsatz.id, 0)),
    );
  }
  const umbruch = neuerSeitenumbruch(naechsteKennung());
  const kennung = naechsteKennung();
  const vorherBlock: Block = block.typ === 'liste' ? { ...block, eintraege: vorher } : absatz(vorher[0], block.id);
  const nachherBlock: Block = block.typ === 'liste' ? neueListe(nachher, kennung) : absatz(nachher[0], kennung);
  const neueBloecke = [...bloecke.slice(0, index), vorherBlock, umbruch, nachherBlock, ...bloecke.slice(index + 1)];
  return fertig(start.dokument, 'text', neueBloecke, kollabiert(position('text', kennung, 0)));
}

/**
 * Entfernt einen manuellen Seitenumbruch und liefert die Schreibposition danach: den Anfang des
 * Blocks, der auf den Umbruch folgte. Gehen dabei zwei Aufzählungen ineinander auf, entfällt die
 * Kennung der zweiten; die Position wandert dann in den entsprechenden Eintrag der zusammengeführten
 * Liste (Kollegenreview !751 vom 29.09.2026: Eine Position mit der alten Kennung wurde auf den
 * Textanfang geklemmt, Weitertippen landete dort). `auswahl` ist null, wenn es den Umbruch nicht
 * gibt (Dokument bleibt dasselbe Objekt) oder ihm kein Block folgt.
 */
export function seitenumbruchEntfernen(
  dokument: Vertragsdokument,
  blockId: string,
): { dokument: Vertragsdokument; auswahl: Auswahl | null } {
  const index = dokument.text.findIndex((block) => block.typ === 'seitenumbruch' && block.id === blockId);
  if (index < 0) return { dokument, auswahl: null };
  const bloecke = dokument.text.filter((_block, stelle) => stelle !== index);
  const folgender = dokument.text.slice(index + 1).find((block) => block.typ !== 'seitenumbruch');
  if (!folgender) {
    return { dokument: mitBloecken(dokument, 'text', normalisiereBloecke(bloecke, 'text').bloecke), auswahl: null };
  }
  return fertig(dokument, 'text', bloecke, kollabiert(position('text', folgender.id, 0)));
}

export function kaestchenUmschalten(
  dokument: Vertragsdokument,
  bereich: Bereich,
  blockId: string,
  eintrag: number,
  knotenIndex: number,
): Vertragsdokument {
  let geaendert = false;
  const bloecke = bloeckeVon(dokument, bereich).map((block) => {
    if (block.id !== blockId) return block;
    const inhalt = inhaltVon(block, eintrag);
    if (!inhalt) return block;
    const knoten = inhalt[knotenIndex];
    if (!knoten || knoten.typ !== 'kaestchen') return block;
    const neuerInhalt = inhalt.slice();
    neuerInhalt[knotenIndex] = { typ: 'kaestchen', angekreuzt: !knoten.angekreuzt };
    geaendert = true;
    return blockMitInhalt(block, eintrag, neuerInhalt);
  });
  return geaendert ? mitBloecken(dokument, bereich, bloecke) : dokument;
}

// ---------------------------------------------------------------------------------------------
// Kopf- und Fußbereich

/**
 * Legt im Kopf- bzw. Fußbereich einen leeren Absatz mit fester Kennung (k1/f1) an, wenn er keinen
 * Inhalt hat; die Normalform macht ihn wieder zu „kein Inhalt“, sobald er leer geblieben ist.
 */
export function bereichBearbeitbarMachen(dokument: Vertragsdokument, bereich: Bereich): Vertragsdokument {
  if (bloeckeVon(dokument, bereich).length > 0) return dokument;
  const bevorzugt = `${PRAEFIX[bereich]}1`;
  const vergeben = ALLE_BEREICHE.some((name) => bloeckeVon(dokument, name).some((block) => block.id === bevorzugt));
  return mitBloecken(dokument, bereich, [absatz([], vergeben ? neueKennung(dokument, bereich) : bevorzugt)]);
}

/** Entfernt einen leer gebliebenen einzigen Absatz aus Kopf- bzw. Fußbereich wieder. */
export function leerenBereichAufraeumen(dokument: Vertragsdokument, bereich: Bereich): Vertragsdokument {
  if (bereich === 'text') return dokument;
  const bloecke = bloeckeVon(dokument, bereich);
  if (bloecke.length > 0 && ohneSichtbarenInhalt(bloecke)) return mitBloecken(dokument, bereich, []);
  return dokument;
}

// ---------------------------------------------------------------------------------------------
// Zwischenablage

/** Sichtbarer Text einer Auswahl (Platzhalter als {{KEY}}, Kästchen als ☐/☒, Umbrüche als Zeilen). */
export function textDerAuswahl(dokument: Vertragsdokument, auswahl: Auswahl): string {
  const sicher = geklemmt(dokument, auswahl);
  if (!sicher) return '';
  const [start, ende] = auswahlSortiert(dokument, sicher);
  const bloecke = bloeckeVon(dokument, start.bereich);
  return abschnitteVon(dokument, start, ende)
    .map(({ blockIndex, eintrag, von, bis }) => {
      const inhalt = inhaltVon(bloecke[blockIndex], eintrag) ?? [];
      const [, rest] = inhaltTeilen(inhalt, von);
      const [mitte] = inhaltTeilen(rest, bis - von);
      return mitte
        .map((knoten) => {
          if (knoten.typ === 'text') return knoten.text;
          if (knoten.typ === 'platzhalter') return `{{${knoten.schluessel}}}`;
          if (knoten.typ === 'kaestchen') return knoten.angekreuzt ? KAESTCHEN_ANGEKREUZT : KAESTCHEN_LEER;
          return '\n';
        })
        .join('');
    })
    .join('\n');
}
